import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ChevronRight as ChevronRightIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  LocationOnOutlined as LocationIcon,
  AttachFile as AttachFileIcon,
  ViewListOutlined as ListViewIcon,
  CalendarMonthOutlined as MonthlyViewIcon,
} from '@mui/icons-material';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import { useHasPermission, SESSION_PERMISSIONS } from 'Utils/permissions';
import SessionStatusPill from './SessionStatusPill';
import SessionsMonthlyView from './SessionsMonthlyView';
import { formatSessionDate, formatSessionTime } from '../utils/session-datetime';

const STATUS_FILTERS = [
  { value: '', labelKey: 'toolbar.filterAll' },
  { value: 'pending', labelKey: 'toolbar.filterPending' },
  { value: 'in_progress', labelKey: 'toolbar.filterInProgress' },
  { value: 'completed', labelKey: 'toolbar.filterCompleted' },
];

const styles = {
  toolbar: {
    p: 2,
    mb: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flexWrap: 'wrap',
    border: `1px solid ${colors.GRAY_HR}`,
    boxShadow: 'none',
    borderRadius: 2,
  },
  search: { flex: '1 1 280px', maxWidth: 360 },
  searchInput: {
    '& .MuiOutlinedInput-root': { borderRadius: 2, height: 38, fontSize: 13.5 },
  },
  filterBtn: active => ({
    px: 1.75,
    py: 0.75,
    borderRadius: 999,
    border: `1px solid ${active ? colors.GREEN : colors.GRAY_HR}`,
    bgcolor: active ? colors.GREEN_ULTRA_LIGHT : '#fff',
    color: active ? colors.GREEN : colors.GRAY_DARKER,
    fontSize: 12.5,
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': { bgcolor: active ? colors.GREEN_ULTRA_LIGHT : colors.BLUE_GRAY },
  }),
  tableCard: {
    overflow: 'hidden',
    border: `1px solid ${colors.GRAY_HR}`,
    boxShadow: 'none',
    borderRadius: 2,
  },
  headerCell: {
    bgcolor: colors.BLUE_GRAY,
    fontSize: 11,
    fontWeight: 600,
    color: colors.GRAY_DARK,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    py: 1.5,
  },
  row: {
    cursor: 'pointer',
    transition: 'background 0.15s',
    '&:hover': { bgcolor: colors.custom.table.row.hover },
  },
  titleCell: { fontWeight: 500, color: colors.BLACK, fontSize: 14, mb: 0.3 },
  meta: { fontSize: 12.5, color: colors.GRAY_DARK, display: 'inline-flex', alignItems: 'center', gap: 0.5, mr: 1.5 },
  primaryBtn: {
    bgcolor: colors.GREEN,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 600,
    px: 2,
    '&:hover': { bgcolor: colors.GREEN_LEAF },
  },
  emptyState: { textAlign: 'center', py: 6, color: colors.GRAY_DARK },
  viewToggle: {
    height: 38,
    '& .MuiToggleButton-root': {
      px: 1.5,
      textTransform: 'none',
      fontSize: 12.5,
      fontWeight: 500,
      color: colors.GRAY_DARKER,
      border: `1px solid ${colors.GRAY_HR}`,
    },
    '& .MuiToggleButton-root.Mui-selected': {
      bgcolor: colors.GREEN_ULTRA_LIGHT,
      color: colors.GREEN,
      borderColor: colors.GREEN,
      '&:hover': { bgcolor: colors.GREEN_ULTRA_LIGHT },
    },
  },
};

export default function SessionsTable({
  sessions,
  loading,
  search,
  statusFilter,
  viewMode,
  onSearchChange,
  onStatusFilterChange,
  onViewModeChange,
  onCreate,
  onOpenDetail,
  onEdit,
  onDelete,
}) {
  const { t2 } = useTranslation('EditCourseSessions');
  const canCreate = useHasPermission(SESSION_PERMISSIONS.CREATE);
  const canUpdate = useHasPermission(SESSION_PERMISSIONS.UPDATE);
  const canDelete = useHasPermission(SESSION_PERMISSIONS.DELETE);

  const rows = sessions || [];

  return (
    <Box>
      <Card sx={styles.toolbar} elevation={0}>
        <TextField
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder={t2('toolbar.searchPlaceholder')}
          size="small"
          sx={styles.search}
          InputProps={{
            sx: styles.searchInput['& .MuiOutlinedInput-root'],
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: colors.GRAY_DARK }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={0.75}>
          {STATUS_FILTERS.map(f => (
            <Button
              key={f.value || 'all'}
              onClick={() => onStatusFilterChange(f.value)}
              sx={styles.filterBtn(statusFilter === f.value)}
              disableElevation
            >
              {t2(f.labelKey)}
            </Button>
          ))}
        </Stack>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, next) => next && onViewModeChange(next)}
          sx={styles.viewToggle}
        >
          <ToggleButton value="list" aria-label="list view">
            <ListViewIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {t2('viewMode.list')}
          </ToggleButton>
          <ToggleButton value="monthly" aria-label="monthly view">
            <MonthlyViewIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {t2('viewMode.monthly')}
          </ToggleButton>
        </ToggleButtonGroup>
        {canCreate && (
          <Button startIcon={<AddIcon />} variant="contained" sx={styles.primaryBtn} onClick={onCreate} disableElevation>
            {t2('toolbar.newSession')}
          </Button>
        )}
      </Card>

      {viewMode === 'monthly' ? (
        <SessionsMonthlyView
          sessions={rows}
          loading={loading}
          onOpenDetail={onOpenDetail}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ) : (
      <Card sx={styles.tableCard} elevation={0}>
        {loading && <LinearProgress sx={{ height: 2 }} />}
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...styles.headerCell, width: 50 }}>{t2('table.rowNumber')}</TableCell>
              <TableCell sx={styles.headerCell}>{t2('table.session')}</TableCell>
              <TableCell sx={styles.headerCell}>{t2('table.date')}</TableCell>
              <TableCell sx={styles.headerCell}>{t2('table.time')}</TableCell>
              <TableCell sx={styles.headerCell}>{t2('table.instructor')}</TableCell>
              <TableCell sx={styles.headerCell}>{t2('table.status')}</TableCell>
              <TableCell sx={{ ...styles.headerCell, width: 140 }}>{t2('table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} sx={styles.emptyState}>
                  <Typography variant="body2" sx={{ mb: 2 }}>{t2('empty.noSessions')}</Typography>
                  {canCreate && (
                    <Button variant="contained" startIcon={<AddIcon />} sx={styles.primaryBtn} onClick={onCreate} disableElevation>
                      {t2('empty.noSessionsCta')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
            {rows.map((session, i) => {
              const instructorLabel = session.instructor?.fullName || session.instructorName || '—';
              const isImmutable = !!session.isManuallyCompleted;
              return (
                <TableRow key={session.id} sx={styles.row} onClick={() => onOpenDetail(session)}>
                  <TableCell sx={{ color: colors.GRAY_DARK, fontWeight: 500 }}>{String(i + 1).padStart(2, '0')}</TableCell>
                  <TableCell>
                    <Typography sx={styles.titleCell}>{session.title}</Typography>
                    <Typography component="div" sx={{ fontSize: 12.5, color: colors.GRAY_DARK }}>
                      {session.location && (
                        <Box component="span" sx={styles.meta}>
                          <LocationIcon sx={{ fontSize: 12 }} />
                          {session.location}
                        </Box>
                      )}
                      {session.evidencesCount > 0 && (
                        <Box component="span" sx={styles.meta}>
                          <AttachFileIcon sx={{ fontSize: 12 }} />
                          {session.evidencesCount} docs
                        </Box>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: colors.GRAY_DARKER, whiteSpace: 'nowrap' }}>
                    {formatSessionDate(session.startDateTime)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: colors.GRAY_DARKER }}>
                    {formatSessionTime(session.startDateTime)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: colors.GRAY_DARKER }}>
                    {instructorLabel}
                  </TableCell>
                  <TableCell>
                    <SessionStatusPill status={session.status} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} onClick={e => e.stopPropagation()}>
                      {canUpdate && (
                        <Tooltip title={isImmutable ? t2('immutable.editDisabledTooltip') : t2('detail.edit')}>
                          <span>
                            <IconButton size="small" disabled={isImmutable} onClick={() => onEdit(session)}>
                              <EditIcon sx={{ fontSize: 18, color: isImmutable ? colors.GRAY : colors.GRAY_DARKER }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title={t2('detail.delete')}>
                          <IconButton size="small" onClick={() => onDelete(session)}>
                            <DeleteIcon sx={{ fontSize: 18, color: colors.RED }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small">
                        <ChevronRightIcon sx={{ fontSize: 18, color: colors.GRAY_DARK }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      )}
    </Box>
  );
}

SessionsTable.propTypes = {
  sessions: PropTypes.array,
  loading: PropTypes.bool,
  search: PropTypes.string,
  statusFilter: PropTypes.string,
  viewMode: PropTypes.oneOf(['list', 'monthly']),
  onSearchChange: PropTypes.func.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onOpenDetail: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

SessionsTable.defaultProps = { sessions: [], loading: false, search: '', statusFilter: '', viewMode: 'list' };
